import React, { useCallback } from 'react';
import { Table, Tag, Space, Button, Tooltip, Image, Typography, Row, Col, Pagination, Dropdown, Divider } from 'antd';
import { useNavigate } from 'react-router-dom';
import { EditOutlined, DeleteOutlined, EyeOutlined, PictureOutlined, SyncOutlined, CaretDownFilled, ShopOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { viewShopifyProductDetail, editShopifyProduct, deleteShopifyProduct, onChangePagination } from '../actions';
import '../ShopifyProducts.css';

const { Text } = Typography;

const ShopifyProductsList = ({ shopifyProducts, isLoading, pagination, onSyncClick }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleViewDetail = (id) => {
    dispatch(viewShopifyProductDetail(id, navigate));
  };

  const handleEdit = (id) => {
    dispatch(editShopifyProduct(id, navigate));
  };

  const handleDelete = async (id) => {
    const result = await dispatch(deleteShopifyProduct(id));
    // Success/error handling is in the action
  };

  const handleTableChange = (pagination) => {
    dispatch(onChangePagination(pagination.current, pagination.pageSize));
  };

  // Sync menu items
  const syncMenuItems = [
    {
      key: 'shopify',
      label: 'Sync products from Shopify',
      icon: <SyncOutlined />
    }
  ];

  const handleSyncMenuClick = ({ key }) => {
    if (key === 'shopify' && onSyncClick) {
      onSyncClick();
    }
  };

  const getStatusTag = (status) => {
    let color = 'default';
    let text = status;

    switch (status) {
      case 'active':
        color = 'green';
        text = 'Active';
        break;
      case 'draft':
        color = 'orange';
        text = 'Draft';
        break;
      case 'archived':
        color = 'red';
        text = 'Archived';
        break;
      default:
        color = 'default';
    }

    return <Tag color={color}>{text}</Tag>;
  };

  const getShopifyStoreText = (item) => {
    // Ưu tiên sử dụng thông tin từ store relation
    if (item.store && item.store.storeName) {
      return item.store.storeName;
    }
    
    return 'Unknown';
  };

  const getMainImage = (images) => {
    if (!images || !Array.isArray(images) || images.length === 0) {
      return null;
    }
    
    // Return first image URL
    return images[0].src || images[0].url;
  };

  const getProductCollections = (record) => {
    const mappings = Array.isArray(record.collectionMappings) ? record.collectionMappings : [];

    return mappings
      .map((mapping) => {
        const collection = mapping.collection || mapping.collectionSnapshot || {};
        const title = collection.title || collection.handle || mapping.shopifyCollectionExternalId;

        if (!title) {
          return null;
        }

        return {
          id: collection.id || mapping.shopifyCollectionExternalId || mapping.id,
          title,
          handle: collection.handle,
          type: collection.collectionType
        };
      })
      .filter(Boolean);
  };

  const renderCollections = (_, record) => {
    const collections = getProductCollections(record);

    if (collections.length === 0) {
      return <Text type="secondary">-</Text>;
    }

    const visibleCollections = collections.slice(0, 2);
    const hiddenCollections = collections.slice(2);
    const hiddenTitle = hiddenCollections.map(collection => collection.title).join(', ');

    return (
      <Space size={[0, 4]} wrap className="shopify-product-collections">
        {visibleCollections.map((collection) => (
          <Tag key={collection.id} color={collection.type === 'smart' ? 'purple' : 'geekblue'}>
            {collection.title}
          </Tag>
        ))}
        {hiddenCollections.length > 0 && (
          <Tooltip title={hiddenTitle}>
            <Tag>+{hiddenCollections.length}</Tag>
          </Tooltip>
        )}
      </Space>
    );
  };

  // Function to set row className for alternating colors
  const getRowClassName = useCallback((record, index) => {
    return index % 2 === 0 ? 'table-row-even' : 'table-row-odd';
  }, []);

  const columns = [
    {
        title: '#',
        key: 'index',
        width: 70,
        render: (_, __, index) => <span style={{fontSize:12}}>{(pagination.current - 1) * pagination.pageSize + index + 1}</span>
    },
    {
      title: <span><PictureOutlined/></span>,
      dataIndex: 'images',
      key: 'images',
      width: 80,
      align: 'center',
      render: (images) => {
        const imageUrl = getMainImage(images);
        
        return imageUrl ? (
          <Image 
            src={imageUrl} 
            className="shopify-product-image" 
            alt="Product"
            width={35}
          />
        ) : (
          <div style={{ width: 35, height: 35, backgroundColor: '#f0f0f0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            No Image
          </div>
        );
      }
    },
    {
      title: 'Product ID',
      dataIndex: 'shopifyProductId',
      key: 'shopifyProductId',
      width: 150,
      render: (text) => (
        <a 
          target='_blank' 
          rel="noopener noreferrer"
          style={{ color: '#1890ff' }} 
          href={`https://admin.shopify.com/store/products/${text}`}
        >
          {text || '-'}
        </a>
      )
    },
    {
      title: 'Product Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Product Type',
      dataIndex: 'productType',
      key: 'productType',
      width: 150,
    },
    {
      title: 'Vendor',
      dataIndex: 'vendor',
      key: 'vendor',
      width: 150,
    },
    {
      title: 'Shopify Store',
      dataIndex: 'store',
      key: 'store',
      width: 150,
      render: (store, record) => (
        <span className="no-line-through">
          <ShopOutlined style={{ marginRight: 4 }} />
          {getShopifyStoreText(record)}
        </span>
      ),
    },
    {
      title: 'Collections',
      dataIndex: 'collectionMappings',
      key: 'collections',
      width: 260,
      render: renderCollections
    },
    {
      title: 'Variants',
      dataIndex: 'variantsCount',
      key: 'variantsCount',
      width: 100,
      align: 'center',
      render: (count, record) => (
        <Tag color="blue">{record.variants?.length || 0}</Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      align: 'center',
      render: (status) => getStatusTag(status)
    }
  ];

  return (
    <>
      <Row justify="space-between" align="middle" className='shopify-products-list-header' style={{ marginBottom: 16 }}>
        <Col>
          <Space direction='horizontal' size={4}>
            <Text strong style={{ fontSize: 14 }}>
              Total Shopify products:
            </Text>
            <Pagination
              size="small"
              current={pagination?.current || 1}
              total={pagination?.total || 0}
              pageSize={pagination?.pageSize || 50}
              onChange={(page, pageSize) => dispatch(onChangePagination(page, pageSize))}
              showSizeChanger={false}
              showQuickJumper={false}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total}`
              }
            />
          </Space>
        </Col>
        <Col>
          <Space>
            <Dropdown
              menu={{
                items: syncMenuItems,
                onClick: handleSyncMenuClick
              }}
              trigger={['hover']}
            >
              <a>
                <SyncOutlined /> Sync <CaretDownFilled />
              </a>
            </Dropdown>
          </Space>
        </Col>
      </Row>

      <div className="shopify-products-list">
        <Table 
          columns={columns}
          dataSource={shopifyProducts.map(item => ({ ...item, key: item.id }))}
          loading={isLoading}
          rowClassName={getRowClassName}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            size: 'small',
            showQuickJumper: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} products`
          }}
          onChange={handleTableChange}
          scroll={{ x: 1360 }}
        />
      </div>
    </>
  );
};

export default ShopifyProductsList;
